/**
 * Employee-chat client hooks.
 *
 * Persistent, resumable conversations with an AI employee (Neo scoped to that
 * employee's Voice Profile + skill). Thin wrappers over /api/employee-chat so
 * the panel can list threads, load one, create new, and send a turn.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, queryKeys } from './api.js';

export interface EmployeeConversation {
  id: string;
  aiEmployeeId: string;
  voiceProfileId: string | null;
  title: string;
  status: string;
  lastMessageAt: string;
  createdAt: string;
}

export interface EmployeeMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'employee';
  body: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export function useEmployeeConversations(employeeId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.employeeChat.conversations(employeeId),
    queryFn: () =>
      api.get<{ conversations: EmployeeConversation[] }>(
        `/api/employee-chat/conversations${employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : ''}`,
      ),
    enabled,
  });
}

export function useEmployeeMessages(conversationId: string | null) {
  return useQuery({
    queryKey: queryKeys.employeeChat.messages(conversationId ?? 'none'),
    queryFn: () =>
      api.get<{ conversation: EmployeeConversation; messages: EmployeeMessage[] }>(
        `/api/employee-chat/conversations/${conversationId}/messages`,
      ),
    enabled: !!conversationId,
  });
}

export function useCreateEmployeeConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aiEmployeeId: string) =>
      api.post<{ conversation: EmployeeConversation }>('/api/employee-chat/conversations', { aiEmployeeId }),
    onSuccess: (_data, aiEmployeeId) => {
      qc.invalidateQueries({ queryKey: queryKeys.employeeChat.conversations(aiEmployeeId) });
      qc.invalidateQueries({ queryKey: queryKeys.employeeChat.conversations(undefined) });
    },
  });
}

export function useSendEmployeeMessage(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<{ userMessage: EmployeeMessage; employeeMessage: EmployeeMessage }>(
        `/api/employee-chat/conversations/${conversationId}/messages`,
        { body },
      ),
    onSuccess: () => {
      if (conversationId) {
        qc.invalidateQueries({ queryKey: queryKeys.employeeChat.messages(conversationId) });
      }
    },
  });
}
